import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  getHandlerRequestsByHandlerId,
  approveHandlerRequest,
  rejectHandlerRequest,
  SaveParticipant,
} from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';
import { useParticipants } from '../hooks/useParticipants';
import { calculateHandlerOpenSlots, getHandlerCapacitySummary } from '../utils/handlerCapacity';
import HandlersRequestsTable from '../../../components/HandlersRequestsTable';
import RejectRequestModal from './RejectRequestModal';

export default function HandlerTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate,
  isEditing,
  onEditingChange,
  saving,
  setSaving 
}) {
  const [handlersRequestsReceived, setHandlersRequestsReceived] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRejectRequest, setSelectedRejectRequest] = useState(null);
  const [showExceedLimitModal, setShowExceedLimitModal] = useState(false);
  const [pendingApprovalRequest, setPendingApprovalRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const { participants: allParticipants } = useParticipants();

  // Local state for editing (mirrors PersonalInfoTab pattern)
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    if (participant) {
      setEditedData({
        ...participant,
        handlerInfo: participant.handlerInfo || {}
      });
    }
  }, [participant]);

  useEffect(() => {
    // When exiting edit mode, ensure we have the latest data
    if (!isEditing && participant) {
      setEditedData(participant);
    }
  }, [isEditing, participant]);

  useEffect(() => {
    if (participant && hasRole('handler')) {
      loadHandlerRequests();
    }
  }, [participant]);



  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  const formatSection = (section) => {
    if (!section || typeof section !== 'string') return 'N/A';
    const s = section.trim().toLowerCase();
    if (s === 'north') return 'North';
    if (s === 'central') return 'Central';
    if (s === 'south') return 'South';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const loadHandlerRequests = async () => {
    try {
      setLoading(true);
      const handlerResult = await getHandlerRequestsByHandlerId(participant.id);
      if (Array.isArray(handlerResult)) {
        setHandlersRequestsReceived(handlerResult);
      } else if (handlerResult && Array.isArray(handlerResult.requests)) {
        setHandlersRequestsReceived(handlerResult.requests);
      } else {
        setHandlersRequestsReceived([]);
      }
    } catch (error) {
      console.error('Failed to load handler requests:', error);
      setHandlersRequestsReceived([]);
      if (onMessage) {
        onMessage('Failed to load handler requests. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId, bypassLimitCheck = false) => {
    try {
      if (!participant || !participant.handlerInfo) {
        onMessage('Handler information not available. Please refresh the page.', 'error');
        return;
      }

      const request = handlersRequestsReceived.find(r => r.id === requestId);
      if (!request) {
        onMessage('Request not found.', 'error');
        return;
      }

      const currentOpenSlots = participant.handlerInfo?.openSlots || 0;
      const birdsRequested = parseInt(request.numberOfBirds || request.birdsRequested) || 0;
      const newOpenSlots = currentOpenSlots - birdsRequested;

      console.log('Approval validation:', {
        requestId,
        birdsRequested,
        currentOpenSlots,
        newOpenSlots,
        bypassLimitCheck
      });

      // Check if this would exceed the bird limit
      if (newOpenSlots < 0 && !bypassLimitCheck) {
        // Show confirmation modal for exceeding limit
        setPendingApprovalRequest(request);
        setShowExceedLimitModal(true);
        return;
      }

      // Proceed with approval
      const result = await approveHandlerRequest(requestId, participant.id);
      
      if (result.success) {
        // Update participant's openSlots
        const updatedParticipant = {
          ...participant,
          handlerInfo: {
            ...participant.handlerInfo,
            openSlots: newOpenSlots
          }
        };

        // Save updated participant data
        const authData = getLoggedInParticipant();
        await SaveParticipant(updatedParticipant, authData?.authToken);

        if (onMessage) {
          onMessage(
            newOpenSlots < 0 
              ? `Request approved! You are now over capacity by ${Math.abs(newOpenSlots)} birds.`
              : 'Request approved successfully!',
            'success'
          );
        }

        // Reload requests and trigger participant data update
        await loadHandlerRequests();
        if (onParticipantUpdate) {
          onParticipantUpdate();
        }
      } else {
        if (onMessage) {
          onMessage(`Failed to approve request: ${result.message}`, 'error');
        }
      }
    } catch (error) {
      console.error('Approval error:', error);
      if (onMessage) {
        onMessage('An error occurred while approving the request.', 'error');
      }
    }
  };

  const handleExceedLimitConfirm = async () => {
    setShowExceedLimitModal(false);
    if (pendingApprovalRequest) {
      await handleApproveRequest(pendingApprovalRequest.id, true); // Bypass limit check
      setPendingApprovalRequest(null);
    }
  };

  const handleExceedLimitCancel = () => {
    setShowExceedLimitModal(false);
    if (pendingApprovalRequest) {
      // Show reject modal instead
      setSelectedRejectRequest(pendingApprovalRequest);
      setShowRejectModal(true);
      setPendingApprovalRequest(null);
    }
  };

  const handleRejectClick = (request) => {
    // Enrich request with breeder information if not present
    const breeder = allParticipants?.find(p => p.id?.toString() === request.breederId?.toString());
    
    const enrichedRequest = {
      ...request,
      breederName: request.breederName || breeder?.fullName || 'Unknown Breeder',
      breederLoftName: request.breederLoftName || breeder?.loftName || 'Unknown Loft'
    };
    
    console.debug('[handleRejectClick] Enriched request:', enrichedRequest);
    setSelectedRejectRequest(enrichedRequest);
    setShowRejectModal(true);
  };

  const handleRejectRequest = async (reason) => {
    try {
      if (!participant || !participant.id) {
        onMessage('Participant information not available. Please refresh the page.', 'error');
        return;
      }
      
      const result = await rejectHandlerRequest(selectedRejectRequest.id, participant.id, reason);
      
      if (result.success) {
        if (onMessage) {
          onMessage('Request rejected and breeder has been notified.');
        }
        await loadHandlerRequests();
        setShowRejectModal(false);
        setSelectedRejectRequest(null);
      } else {
        if (onMessage) {
          onMessage(`Failed to reject request: ${result.message}`);
        }
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred while rejecting the request.');
      }
    }
  };

  const handleSave = async () => {
    // Set saving first to disable the save button immediately
    if (setSaving) {
      setSaving(true);
    }
    
    try {
      const authData = getLoggedInParticipant();
      const result = await SaveParticipant(editedData, authData?.authToken);
      
      // API returns participant object directly, not wrapped in success object
      if (result && result.id) {
        // Exit edit mode immediately
        if (onEditingChange) {
          onEditingChange(false);
        }
        
        // Show loading message while reloading data
        if (onMessage) {
          onMessage('Loading updated information...');
        }
        
        // Trigger participant data update
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Show success message after data is loaded
        if (onMessage) {
          onMessage('Handler information updated successfully!');
        }
      } else {
        if (onMessage) {
          onMessage(result?.message || 'Failed to update handler information. Please try again.');
        }
        // Go back to edit mode if save failed
        if (onEditingChange) {
          onEditingChange(true);
        }
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred while saving handler information.');
      }
      // Go back to edit mode if error occurred
      if (onEditingChange) {
        onEditingChange(true);
      }
    } finally {
      if (setSaving) {
        setSaving(false);
      }
    }
  };

  const handleContactMethodChange = (method, checked) => {
    const currentMethods = editedData.handlerInfo?.breederApplicationMethod || [];
    const newMethods = checked 
      ? [...currentMethods.filter(m => m !== method), method]
      : currentMethods.filter(m => m !== method);
    
    setEditedData(prev => ({
      ...prev,
      handlerInfo: {
        ...(prev.handlerInfo || {}),
        breederApplicationMethod: newMethods
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      handlerInfo: {
        ...(prev.handlerInfo || {}),
        [field]: value
      }
    }));
  };

  // Register save handler with parent component (matches PersonalInfoTab pattern)
  useEffect(() => {
    if (isEditing) {
      // Handler tab uses global save controls
      window.handlerTabSave = handleSave;
    }
    return () => {
      delete window.handlerTabSave;
    };
  }, [isEditing, editedData]);

  // Check if participant data is available
  if (!participant) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading participant information...</div>
      </div>
    );
  }

  if (!hasRole('handler')) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        You need to have the Handler role to access this section. You can add the Handler role in the Personal Info tab.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading handler information...</div>
      </div>
    );
  }

  return (
    <>
      {/* Handler Requests Table */}
      {handlersRequestsReceived.length > 0 && (
        <HandlersRequestsTable
          requests={handlersRequestsReceived}
          allParticipants={allParticipants}
          participant={participant}
          onApproveRequest={handleApproveRequest}
          onRejectClick={handleRejectClick}
        />
      )}

      {/* Handler Information Card */}
      <>
        {/* Alert for missing required handler fields */}
        {participant && (!participant.handlerInfo?.section || !participant.handlerInfo?.totalBirds || participant.handlerInfo?.totalBirds === 0) && (
            <div className="alert alert-warning mb-4 shadow-sm" role="alert">
              <h6 className="alert-heading">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Action Required: Complete Your Handler Profile
              </h6>
              <p className="mb-2">Please update the following required fields to accept breeder requests:</p>
              <ul className="mb-2">
                {!participant.handlerInfo?.section && (
                  <li><strong>Section</strong> - Select your race section (North, Central, or South)</li>
                )}
                {(!participant.handlerInfo?.totalBirds || participant.handlerInfo?.totalBirds === 0) && (
                  <li><strong>Total Birds Capacity</strong> - Specify how many birds you can accept for this race</li>
                )}
              </ul>
              <div className="alert alert-info mt-3">
                <i className="bi bi-info-circle me-2"></i>
                To edit handler settings, click "Edit Profile" above
              </div>
            </div>
          )}
          
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0" style={{ color: '#0b3d91' }}>
                <i className="bi bi-flag me-2"></i>
                Handler Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label fw-bold">Section</label>
                  {isEditing ? (
                    <select
                      className="form-select"
                      value={(editedData.handlerInfo?.section || '').toLowerCase()}
                      onChange={(e) => {
                        handleInputChange('section', e.target.value);
                      }}
                    >
                      <option value="">Select Section</option>
                      <option value="north">North</option>
                      <option value="central">Central</option>
                      <option value="south">South</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={formatSection(participant.handlerInfo?.section) || 'Not assigned'}
                      disabled
                    />
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Total Birds Capacity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={isEditing ? (editedData.handlerInfo?.totalBirds || 0) : (participant.handlerInfo?.totalBirds || '0')}
                    disabled={!isEditing}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value) || 0;
                      handleInputChange('totalBirds', newValue);
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Distance (miles)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={isEditing ? (editedData.handlerInfo?.distanceFromRaceStation || '') : (participant.handlerInfo?.distanceFromRaceStation || '')}
                    placeholder="Distance from race station"
                    disabled={!isEditing}
                    onChange={(e) => {
                      handleInputChange('distanceFromRaceStation', e.target.value);
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Availability Status</label>
                  <div className="pt-2">
                    {(() => {
                      const totalBirds = participant.handlerInfo?.totalBirds;
                      // Calculate approved birds: sum of all approved handler requests
                      let approvedBirds = 0;
                      if (Array.isArray(handlersRequestsReceived)) {
                        approvedBirds = handlersRequestsReceived.reduce((sum, req) => {
                          // Only count requests that are approved
                          if (req.status && req.status.toLowerCase() === 'approved') {
                            const birds = parseInt(req.numberOfBirds || req.birdsRequested) || 0;
                            return sum + birds;
                          }
                          return sum;
                        }, 0);
                      }
                      const openSlots = (totalBirds || 0) - approvedBirds;
                      if (!totalBirds || totalBirds === 0) {
                        return (
                          <span className="badge bg-warning fs-6 px-3 py-2">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Capacity Not Set
                          </span>
                        );
                      }
                      return openSlots > 0 ? (
                        <span className="badge bg-success fs-6 px-3 py-2">{openSlots} Slots Available</span>
                      ) : (
                        <span className="badge bg-secondary fs-6 px-3 py-2">Fully Booked</span>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Contact Methods */}
                <div className="col-12">
                  <label className="form-label fw-bold">Preferred Contact Method for Breeder Requests</label>
                  {isEditing ? (
                    <div className="mt-2">
                      <div className="form-check form-check-inline">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="contactApp" 
                          value="app" 
                          defaultChecked 
                          disabled
                        />
                        <label className="form-check-label" htmlFor="contactApp">
                          <i className="bi bi-app me-1"></i>In-App (Always enabled)
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="contactText" 
                          value="text"
                          checked={editedData.handlerInfo?.breederApplicationMethod?.includes('text')}
                          onChange={(e) => {
                            handleContactMethodChange('text', e.target.checked);
                          }}
                        />
                        <label className="form-check-label" htmlFor="contactText">
                          <i className="bi bi-chat-dots me-1"></i>Text/SMS
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="contactEmail" 
                          value="email"
                          checked={editedData.handlerInfo?.breederApplicationMethod?.includes('email')}
                          onChange={(e) => {
                            handleContactMethodChange('email', e.target.checked);
                          }}
                        />
                        <label className="form-check-label" htmlFor="contactEmail">
                          <i className="bi bi-envelope me-1"></i>Email
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="contactCall" 
                          value="call"
                          checked={editedData.handlerInfo?.breederApplicationMethod?.includes('call')}
                          onChange={(e) => {
                            handleContactMethodChange('call', e.target.checked);
                          }}
                        />
                        <label className="form-check-label" htmlFor="contactCall">
                          <i className="bi bi-telephone me-1"></i>Call
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                      <span className="badge bg-primary fs-6 px-3 py-2">
                        <i className="bi bi-app me-1"></i>
                        In-App
                      </span>
                      {participant.handlerInfo?.breederApplicationMethod?.includes('text') && (
                        <span className="badge bg-info fs-6 px-3 py-2">
                          <i className="bi bi-chat-dots me-1"></i>
                          Text/SMS
                        </span>
                      )}
                      {participant.handlerInfo?.breederApplicationMethod?.includes('email') && (
                        <span className="badge bg-info fs-6 px-3 py-2">
                          <i className="bi bi-envelope me-1"></i>
                          Email
                        </span>
                      )}
                      {participant.handlerInfo?.breederApplicationMethod?.includes('call') && (
                        <span className="badge bg-info fs-6 px-3 py-2">
                          <i className="bi bi-telephone me-1"></i>
                          Call
                        </span>
                      )}
                    </div>
                  )}
                  {!isEditing && (
                    <div className="alert alert-info">
                      <small><i className="bi bi-pencil me-1"></i>Click "Update Information" above to modify handler settings</small>
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="mt-3">
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>You have unsaved changes.</strong> Click "Save Changes" above to save your updates.
                  </div>
                </div>
              )}

              {/* My Breeders Section */}
              {participant.handlerInfo?.breeders && participant.handlerInfo.breeders.length > 0 && (
                <div className="mt-4">
                  <h6 className="fw-bold mb-3">My Breeders ({participant.handlerInfo.breeders.length})</h6>
                  <div className="list-group">
                    {participant.handlerInfo.breeders.map((breeder, idx) => {
                      const breederParticipant = allParticipants?.find(p => p.id === breeder.breederId);
                      
                      return (
                        <div key={idx} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <strong>{breeder.breederName}</strong>
                              <small className="text-muted ms-2">{breeder.loftName}</small>
                              {breederParticipant && (
                                <div className="small mt-1">
                                  {breederParticipant.emailAddress && (
                                    <>
                                      <i className="bi bi-envelope me-1"></i>
                                      <a href={`mailto:${breederParticipant.emailAddress}`} className="text-decoration-none me-3">
                                        {breederParticipant.emailAddress}
                                      </a>
                                    </>
                                  )}
                                  {breederParticipant.mobile && (
                                    <>
                                      <i className="bi bi-phone me-1"></i>
                                      <a href={`tel:${breederParticipant.mobile}`} className="text-decoration-none">
                                        {breederParticipant.mobile}
                                      </a>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="badge bg-primary">{breeder.birds?.length || 0} birds</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>



      {/* Exceed Limit Confirmation Modal */}
      <Modal show={showExceedLimitModal} onHide={() => setShowExceedLimitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Bird Limit Exceeded
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingApprovalRequest && (
            <div>
              <div className="alert alert-warning">
                <strong>You have exceeded your bird limit.</strong>
                <br />
                Are you sure you want to proceed?
              </div>
              <div className="card bg-light">
                <div className="card-body">
                  <strong>Breeder:</strong> {(() => {
                    const breeder = allParticipants?.find(p => p.id === pendingApprovalRequest.breederId);
                    return breeder ? breeder.fullName : 'Unknown Breeder';
                  })()}
                  <br />
                  <strong>Birds Requested:</strong> {pendingApprovalRequest.numberOfBirds || pendingApprovalRequest.birdsRequested}
                  <br />
                  <strong>Current Available Slots:</strong> {participant.handlerInfo?.openSlots || 0}
                  <br />
                  <strong>Slots After Approval:</strong> <span className="text-danger">
                    {(participant.handlerInfo?.openSlots || 0) - (parseInt(pendingApprovalRequest.numberOfBirds || pendingApprovalRequest.birdsRequested) || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleExceedLimitCancel}>
            Reject Instead
          </Button>
          <Button variant="warning" onClick={handleExceedLimitConfirm}>
            Approve Anyway
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Request Modal */}
      {showRejectModal && selectedRejectRequest && (
        <RejectRequestModal
          show={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedRejectRequest(null);
          }}
          request={selectedRejectRequest}
          onSubmit={handleRejectRequest}
        />
      )}
    </>
  );
}